import { i18nFreeGift } from '@/views/promotion/freeGift/FreeGift';
import {FreeGiftGiveStatusType, PostFreeGiftCondition, PostFreeGiftConditionRequest} from 'supporter';
import { addDay, getToday } from '@/utils/dateUtils';
import {
  ConditionNoticeBoxContent,
  ConditionRadioBox, ConditionRegistrationButton, TargetProductInfo,
} from '@/types/promotion';
import { PostProductsSearchByKey } from 'ncp-api-supporter/dist/types/modules/product/product';
import {
  commissionRateColumns,
  giveAvailability, i18nFreeGiftColumnsHeader,
  mallNameColumns, optionPriceColumns, optionSearchKeys,
  partnerNameColumns, priceAndDiscountedPriceColumns, productInfoColumns,
  productOptionInfoColumns, promotionAvailabilityColumns, saleMethodTypeColumns, saleStatusColumns, stockColumns,
} from '@/views/promotion/popup/PromotionOptionPopSearch';
import { pick, size } from 'underscore';
import { FreeGiftStatusType } from 'ncp-api-supporter/dist/types/enum';
import { FREE_GIFT_STATUS_TYPE, GIVE_STATUS_TYPE } from '@/const/promotion';
import { ThrowInputValidationError } from '@/utils/validateUtils';

export const today = getToday();
export const getCurrency = (value: number): string => !value ? '' : value.toLocaleString();

// 안내박스 정보
export const noticeContent: ConditionNoticeBoxContent = {
  register: [
    'CONDITION_REGISTRATION_NOTICE_6',
    'CONDITION_REGISTRATION_NOTICE_7',
    'CONDITION_REGISTRATION_NOTICE_8',
    'CONDITION_REGISTRATION_NOTICE_9'
  ]
}

export const selectBoxOption = {
  option: ['PRODUCT_NAME', 'MALL_PRODUCT_NO', 'PRODUCT_MANAGEMENT_CD', 'MALL_OPTION_NO', 'OPTION_MANAGEMENT_CD'].map(value => ({ name: i18nFreeGift(value, true), value }))
}

// 툴팁
export const tooltip = {
  give: {
    title: '',
    texts: ['TOOLTIP_GIVE_1', 'TOOLTIP_GIVE_2'].map(t => i18nFreeGift(t, true))
  },
  giveOption: {
    title: '',
    texts: ['TOOLTIP_GIVE_OPTION_1', 'TOOLTIP_GIVE_OPTION_2'].map(t => i18nFreeGift(t, true))
  }
}

// 라디오 박스
export const radioOption: ConditionRadioBox = {
  basic: {
    mall: {
      name: 'mall',
      data: [
        {
          id: 'mall-mall',
          value: 'MALL',
          dispaly: 'COMMON_MALL',
        },
        {
          id: 'mall-partner',
          value: 'PARTNER',
          dispaly: 'PARTNER',
        }
      ],
    },
  },
  give: {
    member: {
      name: 'member',
      data: [
        {
          id: 'member-all',
          value: 'ALL',
          dispaly: 'COMMON_ALL',
        },
        {
          id: 'member-select',
          value: 'SELECT',
          dispaly: i18nFreeGift('SELECT_MEMBER', true),
        }
      ],
    },
    limit: {
      name: 'paymentLimit',
      data: [
        {
          id: 'paymentLimit-noLimit',
          value: 'NO_LIMIT',
          dispaly: i18nFreeGift('NO_LIMIT', true),
        },
        {
          id: 'paymentLimit-limit',
          value: 'LIMIT',
          dispaly: '',
        }
      ],
    }
  
  },
  target: {
    product: {
      name: 'product',
      data: [
        {
          id: 'product-all',
          value: 'ALL',
          dispaly: i18nFreeGift('ALL', true),
        },
        {
          id: 'product-product',
          value: 'PRODUCT',
          dispaly: i18nFreeGift('PRODUCT', true),
        },
        {
          id: 'product-brand',
          value: 'BRAND',
          dispaly: i18nFreeGift('BRAND', true),
        },
        {
          id: 'product-display',
          value: 'DISPLAY_CATEGORY',
          dispaly: i18nFreeGift('DISPLAY', true),
        },
        {
          id: 'product-partner',
          value: 'PARTNER',
          dispaly: i18nFreeGift('PARTNER', true),
        },
      ],
    },
    excludeTarget: {
      name: 'excludeTarget',
      data: [
        {
          id: 'excludeTarget-notUse',
          value: 'NOT_USE',
          dispaly: i18nFreeGift('NOT_USE', true),
        },
        {
          id: 'excludeTarget-use',
          value: 'USE',
          dispaly: i18nFreeGift('USE', true),
        },
      ],
    },
  }
  
}

// 기간
export const periodOption = {
  give: {
    dateType: 'YmdHm',
    havePeriodBtn: false,
    name: 'giveInformation',
    fromRanges: today
  }
}


// 지급관리 그리드
export const gridProps = () => ({
  header: {
    height: 33,
  },
  options: {
    columnOptions: {
      resizable: true,
    },
    rowHeight: 31,
    bodyHeight: 600,
    displayRowCount: 15,
    isInitialRequest: true,
    useClientSort: false,
    minRowHeight: 31,
    pageOptions: {
      page: 1,
      perPage: 30,
      totalCount: 0
    }
  },
  columns: i18nFreeGiftColumnsHeader([
    {
      header: 'GRID_TITLE_GIVE_CONDITION_NO', // 지급번호
      name: 'giveConditionNo',
      align: 'center',
      minWidth: 75,
      formatter: ({ value }) => `<a href="/promotion/free-gift/condition/edit/${value}">${value}</a>`
    },
    {
      header: 'GRID_TITLE_MALL_NAME', // 쇼핑몰
      name: 'mallName',
      align: 'center',
      minWidth: 120,
      copyOptions: {
        useFormattedValue: true
      }
    },
    {
      header: 'GRID_TITLE_GIVE_CONDITION_NAME', // 지급조건명
      name: 'giveConditionName',
      align: 'center',
      minWidth: 120,
    },
    {
      header: 'GRID_TITLE_GIVE_STATUS_TYPE', // 지급상태
      name: 'giveStatusType',
      align: 'center',
      minWidth: 100,
      formatter: ({ value }) => i18nFreeGift(value)
    },
    {
      header: 'GRID_TITLE_GIVE_PERIOD', // 지급기간
      name: 'giveStartDateTime',
      align: 'left',
      minWidth: 280,
      formatter: ({ row, value }) => `${value} ~ ${row.giveEndDateTime}`
    },
    {
      header: 'GRID_TITLE_REGISTER_NAME', // 지급등록자
      name: 'registerName',
      align: 'center',
      minWidth: 30,
    },
    {
      header: 'GRID_TITLE_REGISTER_DATE_TIME', // 등록일시
      name: 'registerDateTime',
      align: 'center',
      minWidth: 150,
    },
    {
      header: 'GRID_TITLE_UPDATER_NAME', // 최종수정자
      name: 'updaterName',
      align: 'center',
      minWidth: 30,
      formatter: ({ value }) => value ?? '-'
    },
    {
      header: 'GRID_TITLE_UPDATER_DATE_TIME', // 최종수정일
      name: 'updateDateTime',
      align: 'center',
      minWidth: 150,
      formatter: ({ value }) => value ?? '-'
    }
  ]),
})

// 지급조건 등록
export const registrationForm = (): PostFreeGiftConditionRequest['data'] => {
  return {
    mallNo: undefined,
    mallShareRate: 100,
	  excludeTargets: [],
	  targetInfo: {
		  targetType: 'ALL',
			targets: [],
	  },
    // targetNos: null,
    freeGifts: null,
    giveConditionName: '',
    giveConditionExplain: '',
    limitedMember: {
      memberGrade: [],
      memberGroup: [],
    },
    // targetType: 'ALL',
    giveStartDateTime: today,
    giveEndDateTime: addDay(today,30),
    nonMemberAvailable: false,
    upperPrice: null,
  }
}

// 지급조건 등록 벨리데이션
export const checkErrorOnRegistrationForm = (data: PostFreeGiftCondition, { isPaymentLimit, useExcludeTargetType }) => {
  if (!data.mallNo) ThrowInputValidationError.of('mallNo', 'ALERT_SELECT_MALL');

  if (!data.giveConditionName) ThrowInputValidationError.of('giveConditionName', 'PLZ_GIVE_CONDITION_NAME');
  
  if (data.limitedMember !== null && !hasSize(data.limitedMember.memberGrade) && !hasSize(data.limitedMember.memberGroup)) ThrowInputValidationError.of('memberGrade', 'PLZ_SELECT_MEMBER');

  if(isPaymentLimit && !data.upperPrice) ThrowInputValidationError.of('upperPrice', 'PLZ_UPPER_PRICE');

  if(data.targetInfo.targetType !== 'ALL' && !hasSize(data.targetInfo.targets)) ThrowInputValidationError.of('targetNos', 'PLZ_SELECT_TARGET_NOS')

  if (useExcludeTargetType && !hasSize(data.excludeTargets)) ThrowInputValidationError.of('excludeTargetProductNos', 'PLZ_SELECT_EXCLUDE_TARGET_NOS')

  if(!hasSize(data.freeGifts)) ThrowInputValidationError.of('freeGifts', 'PLZ_SELECT_FREE_GIFTS');
  
}

// 지급상태 변경 버튼정보
export const giveStatusButton: ConditionRegistrationButton<FreeGiftStatusType> = {
  [FREE_GIFT_STATUS_TYPE.STOP]: {
    label: 'STOP_BTN',
    show: false,
    value: FREE_GIFT_STATUS_TYPE.STOP,
    confirmMsg: 'CONFIRM_STOP_GIVE_STATUS'
  },
  [FREE_GIFT_STATUS_TYPE.AVAILABLE]: {
    label: 'AVAILABLE_BTN',
    show: false,
    value: FREE_GIFT_STATUS_TYPE.AVAILABLE,
    confirmMsg: 'CONFIRM_RESTART_GIVE_STATUS'
  },
  [FREE_GIFT_STATUS_TYPE.FINISH]: {
    label: 'FINISH_BTN',
    show: false,
    value: FREE_GIFT_STATUS_TYPE.FINISH,
    confirmMsg: 'CONFIRM_FINISH_GIVE_STATUS'
  },
};
const immutable = {
  [GIVE_STATUS_TYPE.ON]: ['giveStartDateTime', 'limitedMember', 'upperPrice', 'mallShareRate', 'freeGifts'],
  [GIVE_STATUS_TYPE.FINISH]: ['giveConditionName', 'giveConditionExplain', 'giveEndDateTime', 'targetInfo'],
}
export const immutables = (status: FreeGiftGiveStatusType): string[] => {
  const availableStatusImmutables = immutable[GIVE_STATUS_TYPE.ON];
  
  if (status === GIVE_STATUS_TYPE.ON || status === GIVE_STATUS_TYPE.STOP) {
    return availableStatusImmutables
  }
  if (status === GIVE_STATUS_TYPE.FINISH) {
    return availableStatusImmutables.concat(immutable[GIVE_STATUS_TYPE.FINISH]);
  }
  return []
}

// 사은품 대상 설정 > 개별상품 그리드
export const productGridProps = (url: string) => ({
  name: '',
  header: {
    height: 33,
  },
  options: {
    columnOptions: {
      resizable: true,
    },
    rowHeaders: ['checkbox'],
    rowHeight: 31,
    bodyHeight: 600,
    displayRowCount: 15,
    isInitialRequest: true,
    useClientSort: false,
    minRowHeight: 31,
    keyColumnName: 'productNo',
    pageOptions: {
      page: 1,
      perPage: 30,
      totalCount: 0
    }
  },
  columns: i18nFreeGiftColumnsHeader([
    ...mallNameColumns,
    {
      header: 'GRID_TITLE_PARTNER_NAME', // 파트너사
      name: 'partnerNo',
      align: 'center',
      minWidth: 75,
    },
    ...productInfoColumns(url),
    ...saleMethodTypeColumns(),
    ...commissionRateColumns,
    ...priceAndDiscountedPriceColumns,
    {
      header: 'GRID_TITLE_ADD_DATE_TIME', // 추가일
      name: 'registerDateTime',
      align: 'center',
      minWidth: 85,
    },
  ])
});

// 사은품 지급 옵션 그리드
export const optionGridProps = (url, isDisabled?: boolean) => ({
  noPagination: true,
  scrollX: true,
  header: {
    height: 33,
  },
  options: {
    columnOptions: {
      resizable: true,
    },
    rowHeaders: isDisabled ? [] : ['checkbox'],
    rowHeight: 31,
    bodyHeight: 600,
    displayRowCount: 16,
    isInitialRequest: true,
    useClientSort: false,
    minRowHeight: 31,
    keyColumnName: 'optionNo',
    pageOptions: {
      useClient: false,
    }
  },
  columns: i18nFreeGiftColumnsHeader([
    ...mallNameColumns,
    ...partnerNameColumns,
    ...productInfoColumns(url),
    ...productOptionInfoColumns,
    ...giveAvailability,
    ...stockColumns,
    // ...saleMethodTypeColumns(),
    ...saleStatusColumns(),
    ...promotionAvailabilityColumns('freeGift'),
    ...priceAndDiscountedPriceColumns,
    ...optionPriceColumns,
    ...commissionRateColumns,
  ]),
})

export const gridOption = {
  haveExcelDownBtn: false,
  havePageSizeBtn: false,
  columnOptions: {
    resizable: true
  }
}

export const targetInfoKeys = [
  'mallNo',
  'mallName',
  'partnerNo',
  'partnerName',
  'productNo',
  'productName',
  'saleMethodType',
  'commissionRate',
  'supplyPrice',
  'purchasePrice',
  'price'
];

export const mapSearchedProducts = (data: PostProductsSearchByKey[], originSelectedTargetMap): TargetProductInfo[] => {
	return data.map(product => {
    const { mallProductNo, commission, saleMethodType } = product;
    const commissionRate = commission.type === 'PURCHASE_PRICE' || saleMethodType === 'PURCHASE' ? 0 : commission.rate;
    return  {
      ...pick(product, optionSearchKeys),
      productNo: mallProductNo,
      commissionRate,
      registerDateTime: originSelectedTargetMap[mallProductNo] ?? null,
    } as TargetProductInfo
  })
}

/**
 * 시간을 설정하는 셀렉터가 한 시간 단위로 설정할 수 있기 때문에
 * 저장된 시간까지만 사용하고 분과 초는 셀렉터 조건에 맞춰 만들어주는 함수
 * 예)
 * dateTime (start) = '2000-12-13 14:14:12' -> '2000-12-13 14:00:00'
 * dateTime (end) = '2000-12-13 14:14:12' -> '2000-12-13 14:59:59'
 *
 * @param dateTime 'YYYY-MM-DD HH:MM:SS'
 * @param type : 'start' | 'end'
 * @return 'YYYY-MM-DD HH:00:00' | 'YYYY-MM-DD HH:59:59'
 */
export const dateTimeGenerator = (dateTime: string, type: 'start' | 'end'): string => {
  const [date, time] = dateTime.split(' ');
  const min = type === 'start' ? '00:00' : '59:59';
  return `${ date } ${ time.split(':')[0]}:${min}`
}

export const hasSize = (data): boolean => size(data) > 0;
